import { useQuery } from "@apollo/client";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { IssueListButton } from "components/Forms/IssueListButton";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { Issue, WasteShipmentStatus } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { hasCriticalIssues } from "util/forms";
import { openInNewTab } from "util/openInNewTab";

import { AddContainersDialog } from "./AddContainersDialog";
import { GET_WASTE_SHIPMENT_QUERY } from "./api";
import { EditLineQuantityDialog } from "./EditLineQuantityDialog";
import { MarkShipmentShippedDialog } from "./MarkShipmentShippedDialog";
import { attachedDocumentNoun, cancelDetachWarning } from "./reconciliationDisplay";
import { RequestPickupDialog } from "./RequestPickupDialog";
import { ShipmentContentsCard } from "./ShipmentContentsCard";
import {
  ShipmentAlerts,
  ShipmentDetailSkeleton,
  ShipmentInformationCard,
  ShipmentPageGrid,
  ShipmentPageShell,
  ShipmentRail,
} from "./ShipmentDetailChrome";
import {
  useAbandonShipment,
  useReconciliationDiffs,
  useSaveShipment,
  useShipmentHeaderForm,
} from "./shipmentDetailHooks";
import { ShipmentDetailsFacts, ShipmentDetailsFields } from "./ShipmentDetailsCard";
import { Shipment } from "./shipmentDetailTypes";
import { SAVE_BEFORE_ACTING } from "./shipmentDisplay";
import { ShipmentManifestSection } from "./ShipmentManifestSection";
import { ShipmentPageHeader } from "./ShipmentPageHeader";
import { shipmentStepActions } from "./shipmentStepActions";
import { DisplayLine, useStagedShipmentLines } from "./useStagedShipmentLines";

/**
 * Why the contents cannot be edited, or undefined when they can.
 *
 * An attached manifest freezes them: it is the regulated record of what left
 * the site, so editing the contents afterwards would silently invalidate the
 * line matches. The server enforces the lock off the link column
 * (`linkedProcessedManifestId`), not off `manifestReceivedAt` — that field only
 * records when a document was received, not whether one is currently attached. `attachedDocumentKind`
 * is the resolver's own answer to "which, if either, is attached", so keying
 * this lock off it (rather than re-deriving the same fact from a different
 * column) is what keeps this check from disagreeing with the server about
 * when it applies.
 */
function contentLockReasonFor(shipment: Shipment): string | undefined {
  if (shipment.reconciliation.attachedDocumentKind == null) {
    return undefined;
  }
  const noun = attachedDocumentNoun(shipment.reconciliation.attachedDocumentKind);
  return `Detach the ${noun} to change this shipment's contents.`;
}

const isEditable = (status: WasteShipmentStatus) =>
  status === WasteShipmentStatus.Draft || status === WasteShipmentStatus.Requested;

/**
 * The rare and irreversible actions, kept out of the header's main row so the
 * one contained button is unambiguously the next step.
 *
 * Only those. The request document used to live here too, which put "open the
 * PDF we sent the vendor" one item above "delete this shipment" — two things
 * with nothing in common but being hard to place. It now sits under the
 * Request pickup step in the rail, beside the milestone that produced it, and
 * this menu is left holding exactly what its name claims.
 *
 * Not the shared `ActionsMenu`: that renders `size="large"` and collapses to a
 * plain button when it holds a single item, so it would change size and shape
 * as the shipment moves through its statuses. This stays an icon at the page's
 * small control size.
 */
function ShipmentOverflowMenu({
  shipment,
  onAbandon,
}: {
  shipment: Shipment;
  onAbandon: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const run = (action: () => void) => () => {
    close();
    action();
  };

  const isDraft = shipment.status === WasteShipmentStatus.Draft;

  const items: { key: string; label: string; onClick: () => void; disabled?: boolean }[] = [];
  if (isEditable(shipment.status)) {
    items.push({
      key: "abandon",
      label: isDraft ? "Delete shipment" : "Cancel shipment",
      onClick: run(onAbandon),
    });
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          aria-label="More actions"
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {items.map((item) => (
          <MenuItem key={item.key} onClick={item.onClick} disabled={item.disabled}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * The page's commit point, in the header card rather than a bar floating over
 * the bottom of the viewport.
 *
 * There is no edit mode to enter. An editable shipment is simply editable: the
 * fields are fields and the container rows carry their own actions, so the page
 * never changes shape underneath the reader. That leaves nothing for these
 * controls to do until something has actually been changed, which is why they
 * render only once there is a change to commit — an idle page has no Save to
 * disable and no count of nothing to report.
 *
 * The header fields and the container changes share one Save, because the
 * shipment commits as a single unit. Discard drops the staged edits rather than
 * navigating: here the form IS the page, so going back would take the user off
 * the shipment entirely.
 */
function ShipmentEditActions({
  unsavedCount,
  issues,
  saving,
  onSave,
  onDiscard,
}: {
  unsavedCount: number;
  issues: Issue[];
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  if (unsavedCount === 0) {
    return null;
  }
  const blocked = hasCriticalIssues(issues);

  return (
    <>
      {/* The only thing that counts a staged container removal: the rows show
          themselves struck through, but nothing else totals them. */}
      <Typography variant="caption" color="text.secondary">
        {unsavedCount} unsaved {pluralize("change", unsavedCount)}
      </Typography>
      <IssueListButton issues={issues} />
      <Button size="small" onClick={onDiscard}>
        Discard
      </Button>
      <Tooltip title={blocked ? "Resolve the issues above first" : ""}>
        <span>
          <Button
            size="small"
            variant="contained"
            loading={saving}
            disabled={blocked}
            onClick={onSave}
          >
            Save changes
          </Button>
        </span>
      </Tooltip>
    </>
  );
}

export function WasteShipmentDetail() {
  const { tenantId } = useTenant();
  const { shipmentId } = useParams<{ shipmentId: string }>();

  const { data, loading } = useQuery(GET_WASTE_SHIPMENT_QUERY, {
    variables: { id: shipmentId ?? "" },
    skip: !shipmentId,
  });
  const shipment = data?.wasteShipment ?? null;

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments", to: tenantId ? `/o/${tenantId}/waste/shipments` : undefined },
    // No placeholder crumb: a generic "Shipment" that swaps to the real number
    // a moment later reads as the page changing its mind. Better to have the
    // trail arrive one step short and then complete.
    ...(shipment ? [{ label: shipment.manifestRequestNumber }] : []),
  ]);

  if (loading && !shipment) {
    return <ShipmentDetailSkeleton />;
  }

  if (!shipment) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">Shipment not found</Typography>
      </Box>
    );
  }

  // Keyed on the shipment so a different one starts with clean staged state.
  return <LoadedShipmentDetail key={shipment.id} shipment={shipment} />;
}

/**
 * The shipment page edits as one unit: the header fields and the container
 * changes share a single Save and Discard. Removing a container and thinking
 * better of it should cost nothing, and a shipment should never be
 * half-edited — particularly once its request is in a vendor's hands.
 */

/**
 * Everything this page can open over itself.
 *
 * Grouped rather than left inline because none of them is part of the page's
 * own layout — each is a step the user takes from it, and reading the page
 * shouldn't mean reading five dialogs first.
 */
function ShipmentDetailDialogs({
  shipment,
  staged,
  isDraft,
  dialogs,
  onAbandon,
  abandonInFlight,
}: {
  shipment: Shipment;
  staged: ReturnType<typeof useStagedShipmentLines>;
  isDraft: boolean;
  dialogs: ReturnType<typeof useShipmentDialogState>;
  onAbandon: () => void;
  abandonInFlight: boolean;
}) {
  const {
    addOpen,
    setAddOpen,
    shipOpen,
    setShipOpen,
    requestOpen,
    setRequestOpen,
    editLine,
    setEditLine,
    cancelOpen,
    setCancelOpen,
  } = dialogs;

  return (
    <>
      {addOpen && (
        <AddContainersDialog
          shipment={shipment}
          stagedContainerIds={staged.stagedContainerIds}
          onStage={staged.addContainers}
          onClose={() => setAddOpen(false)}
        />
      )}
      {shipOpen && (
        <MarkShipmentShippedDialog shipment={shipment} onClose={() => setShipOpen(false)} />
      )}
      {requestOpen && (
        <RequestPickupDialog shipment={shipment} onClose={() => setRequestOpen(false)} />
      )}
      {editLine?.lineId && (
        <EditLineQuantityDialog
          shipmentId={shipment.id}
          line={{ ...editLine, lineId: editLine.lineId }}
          onStage={staged.setQuantity}
          onClose={() => setEditLine(null)}
        />
      )}
      {cancelOpen && (
        <Dialog
          open
          onClose={() => setCancelOpen(false)}
          maxWidth="xs"
          title={isDraft ? "Delete this shipment?" : "Cancel this shipment?"}
          primaryActionButtonProps={{
            saveText: isDraft ? "Delete shipment" : "Cancel shipment",
            loading: abandonInFlight,
            onClick: onAbandon,
          }}
        >
          <AbandonShipmentExplanation shipment={shipment} isDraft={isDraft} />
        </Dialog>
      )}
    </>
  );
}

/**
 * The two-column middle of the page: what the shipment is on the left, and the
 * steps it moves through on the right.
 */
function ShipmentDetailBody({
  shipment,
  form,
  staged,
  canEdit,
  isDraft,
  hasUnsaved,
  requestDocumentExists,
  onRequestPickup,
  onMarkShipped,
}: {
  shipment: Shipment;
  form: ReturnType<typeof useShipmentHeaderForm>;
  staged: ReturnType<typeof useStagedShipmentLines>;
  canEdit: boolean;
  isDraft: boolean;
  hasUnsaved: boolean;
  requestDocumentExists: boolean;
  onRequestPickup: () => void;
  onMarkShipped: () => void;
}) {
  return (
    <ShipmentPageGrid
      body={
        <>
          <ShipmentInformationCard shipment={shipment} />

          {/* Full width now that the manifest has moved into the rail: six
                fields sharing a row with a card had ~420px to work with. */}
          {canEdit ? (
            <ShipmentDetailsFields form={form} />
          ) : (
            <ShipmentDetailsFacts shipment={shipment} />
          )}
        </>
      }
      rail={
        <ShipmentRail
          shipment={shipment}
          stepActions={{
            ...shipmentStepActions({
              canEdit,
              isDraft,
              isRequested: shipment.status === WasteShipmentStatus.Requested,
              hasUnsaved,
              requestExportUrl: shipment.requestExportUrl,
              requestDocumentExists,
              onRequestPickup,
              onMarkShipped,
              onViewRequest: () =>
                shipment.requestExportUrl && openInNewTab(shipment.requestExportUrl),
            }),
            manifest: (
              <ShipmentManifestSection
                shipment={shipment}
                // Staged CONTAINER changes only, not header edits: attaching
                // freezes the contents, so a pending add or removal would
                // become unsaveable the moment a manifest lands. Header edits
                // are unaffected by the lock, so they must not block it.
                attachBlockedReason={
                  staged.changeCount > 0
                    ? "Save or discard your container changes first — attaching a manifest freezes this shipment's contents."
                    : undefined
                }
              />
            ),
          }}
        />
      }
    />
  );
}

/** Which of the page's dialogs is open. Grouped so the page reads as a page. */
function useShipmentDialogState() {
  const [addOpen, setAddOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [editLine, setEditLine] = useState<DisplayLine | null>(null);

  return {
    addOpen,
    setAddOpen,
    cancelOpen,
    setCancelOpen,
    shipOpen,
    setShipOpen,
    requestOpen,
    setRequestOpen,
    editLine,
    setEditLine,
  };
}

/** The header's action row: commit controls while editable, overflow always. */
function ShipmentHeaderActions({
  shipment,
  canEdit,
  unsavedCount,
  issues,
  saving,
  onSave,
  onDiscard,
  onAbandon,
}: {
  shipment: Shipment;
  canEdit: boolean;
  unsavedCount: number;
  issues: Issue[];
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onAbandon: () => void;
}) {
  return (
    <>
      {canEdit && (
        <ShipmentEditActions
          unsavedCount={unsavedCount}
          issues={issues}
          saving={saving}
          onSave={onSave}
          onDiscard={onDiscard}
        />
      )}
      <ShipmentOverflowMenu shipment={shipment} onAbandon={onAbandon} />
    </>
  );
}

function LoadedShipmentDetail({ shipment }: { shipment: Shipment }) {
  const dialogs = useShipmentDialogState();
  const { setAddOpen, setCancelOpen, setShipOpen, setRequestOpen, setEditLine } = dialogs;
  /** Set while deleting, so the leave-guard doesn't argue about a doomed draft. */
  const [abandoning, setAbandoning] = useState(false);

  const staged = useStagedShipmentLines(shipment);
  const form = useShipmentHeaderForm(shipment);
  const {
    formState: { dirtyFields },
    issues: headerIssues,
  } = form;

  const { diffsByStream, manifestOnlyDiffs, shipmentLevelDiffs, uncharacterizedDiffs } =
    useReconciliationDiffs(shipment);

  /*
    Whether a request document was ever produced, asked of `requestGeneratedAt`
    rather than `requestExportUrl`. The URL is an S3 key signed on every read and
    comes back null whenever signing fails, so a document that does exist would
    read as none — the wrong-facts case the stale-request alert exists to prevent.
    The timestamp is written beside the key and cannot fail.

    Every surface that describes what happened reads this one value: the alert,
    and the rail's request button. The link itself is still what decides whether
    View is offered, since that is a question about right now. Splitting the two
    is what stops a signing failure making the page contradict itself.
  */
  const requestDocumentExists = shipment.requestGeneratedAt != null;

  // Editable or not, with no mode in between. A shipment the vendor has not
  // taken yet is one the user can change, so its fields are fields; once it has
  // shipped or been canceled the record is closed and they are facts. Toggling
  // between the two on a shipment that was editable all along only made the
  // page change shape.
  const canEdit = isEditable(shipment.status);
  const unsavedCount = Object.keys(dirtyFields).length + staged.changeCount;
  const hasUnsaved = unsavedCount > 0;

  // An attached manifest freezes the contents. The manifest is the regulated
  // record of what left the site, and its lines are matched to these containers
  // — editing them afterwards would silently invalidate those links. The server
  // enforces this; saying so here means the user learns it from the disabled
  // control rather than from a rejected save.
  const contentLockReason = contentLockReasonFor(shipment);

  const { handleSave, handleDiscard, saving } = useSaveShipment({ shipment, form, staged });

  // A draft never left the building, so it is deleted rather than left behind
  // as a canceled row; once requested, the vendor has its number and the
  // record stays.
  const isDraft = shipment.status === WasteShipmentStatus.Draft;

  const { handleAbandon, abandoning: abandonInFlight } = useAbandonShipment({
    shipment,
    isDraft,
    onDiscard: handleDiscard,
    onDone: () => setCancelOpen(false),
    setAbandoning,
  });

  return (
    <ShipmentPageShell
      header={
        <ShipmentPageHeader
          shipment={shipment}
          actions={
            <ShipmentHeaderActions
              shipment={shipment}
              canEdit={canEdit}
              unsavedCount={unsavedCount}
              issues={headerIssues}
              saving={saving}
              onSave={handleSave}
              onDiscard={handleDiscard}
              onAbandon={() => setCancelOpen(true)}
            />
          }
        />
      }
    >
      {/*
        The page has no edit mode to leave, and its Save sits at the top of a
        long page — so an edit can scroll out of sight and be walked away from
        without a trace. Staged container removals are the ones that would hurt:
        they live only in this component's state.
      */}
      <UnfinishedChangesPrompt when={hasUnsaved && !abandoning} />

      <ShipmentAlerts
        issues={shipment.issues}
        requestStale={canEdit && shipment.manifestRequestStale}
        requestDocumentExists={requestDocumentExists}
        resendBlockedReason={hasUnsaved ? SAVE_BEFORE_ACTING : undefined}
        onResendRequest={canEdit ? () => setRequestOpen(true) : undefined}
      />

      <ShipmentDetailBody
        shipment={shipment}
        form={form}
        staged={staged}
        canEdit={canEdit}
        isDraft={isDraft}
        hasUnsaved={hasUnsaved}
        requestDocumentExists={requestDocumentExists}
        onRequestPickup={() => setRequestOpen(true)}
        onMarkShipped={() => setShipOpen(true)}
      />

      {/* Full width, below the rail: the contents are the widest thing on the
          page and the only part that is a table. */}
      <ShipmentContentsCard
        shipment={shipment}
        lines={staged.displayLines}
        editable={canEdit}
        contentLockReason={contentLockReason}
        issues={shipment.issues}
        onAddContainers={() => setAddOpen(true)}
        onEditLine={setEditLine}
        onRemoveLine={staged.removeLine}
        onRestoreLine={staged.restoreLine}
        diffsByStream={diffsByStream}
        manifestOnlyDiffs={manifestOnlyDiffs}
        shipmentLevelDiffs={shipmentLevelDiffs}
        uncharacterizedDiffs={uncharacterizedDiffs}
        attachedDocumentKind={shipment.reconciliation.attachedDocumentKind}
      />

      <ShipmentDetailDialogs
        shipment={shipment}
        staged={staged}
        isDraft={isDraft}
        dialogs={dialogs}
        onAbandon={handleAbandon}
        abandonInFlight={abandonInFlight}
      />
    </ShipmentPageShell>
  );
}

/**
 * What abandoning this shipment will actually do.
 *
 * Three cases rather than two, and its own component because they cannot be a
 * ternary chain. The third is the one that was missing: canceling releases an
 * attached manifest — the link, the received date, the signed flag and every
 * line's match to a manifest line — and nothing said so. Losing the tie to a
 * signed regulated document on an unlabelled confirm is not a surprise anyone
 * should get, least of all on an action with no undo.
 */
function AbandonShipmentExplanation({
  shipment,
  isDraft,
}: {
  shipment: Shipment;
  isDraft: boolean;
}) {
  if (isDraft) {
    return (
      <Typography variant="body2">
        {shipment.manifestRequestNumber} has not gone to a vendor, so it will be deleted outright.
        Its containers are untouched and stay available to package.
      </Typography>
    );
  }

  // The shipment's own snapshot first: it survives the manifest row being
  // rebuilt by the pipeline, so it is what can always be named.
  const trackingNumber =
    shipment.reconciliation.linkedManifestTrackingNumber ??
    shipment.manifest?.manifestTrackingNumber ??
    null;
  // Keyed off the resolver's own answer, not off `manifestReceivedAt`, which
  // only records when a document was received.
  const attachedKind = shipment.reconciliation.attachedDocumentKind ?? null;
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        {shipment.manifestRequestNumber} will be marked canceled and kept, because the vendor
        already has its request. No container amounts change — removals only happen when a shipment
        ships.
      </Typography>
      {attachedKind != null && (
        <Alert severity="warning">
          {cancelDetachWarning({
            kind: attachedKind,
            trackingNumber,
          })}{" "}
          It stays in Encamp and can be attached to whichever shipment really carried the waste.
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        Canceling cannot be undone.
      </Typography>
    </Stack>
  );
}

/**
 * The shipment's header as a live inline form — the single place vendor,
 * transporter, TSDF, and dates are collected. Save/Discard appear only when
 * dirty; background refetches keep in-progress edits (keepDirtyValuesOnReset).
 * 3C note: once requests generate, a header edit after requestGeneratedAt
 * should surface a stale-request warning here.
 */

/**
 * The ship dialog's starting date: the planned ship date when there is one and
 * it has arrived, otherwise today. A planned date still in the future would be
 * rejected as a future shipped date, so it is not offered.
 */
